import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";

const baseUrl = `${config.apiUrl}/flows`;

export const flowsApi = {
  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch flows: ${res.statusText}`);
    return res.json();
  },

  async get(id: string, includeSteps = false, includeConnections = false) {
    const params = new URLSearchParams();
    if (includeSteps) params.set("includeSteps", "true");
    if (includeConnections) params.set("includeConnections", "true");
    const url = `${baseUrl}/${id}${params.toString() ? `?${params.toString()}` : ""}`;
    const res = await fetch(url, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch flow: ${res.statusText}`);
    return res.json();
  },

  async create(input: {
    title: string;
    description?: string;
    purpose?: string;
    departmentId?: string;
    status?: "active" | "draft" | "inactive";
  }) {
    const formData = new FormData();
    formData.set("title", input.title);
    if (input.description) formData.set("description", input.description);
    if (input.purpose) formData.set("purpose", input.purpose);
    if (input.departmentId) formData.set("departmentId", input.departmentId);
    if (input.status) formData.set("status", input.status);

    const res = await fetch(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create flow: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async duplicate(id: string) {
    const res = await fetch(`${baseUrl}/${id}/duplicate`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to duplicate flow: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async update(
    id: string,
    input: {
      title?: string;
      description?: string;
      purpose?: string;
      departmentId?: string;
      status?: "active" | "draft" | "inactive";
    },
  ) {
    const formData = new FormData();
    if (input.title) formData.set("title", input.title);
    if (input.description !== undefined) {
      if (input.description) formData.set("description", input.description);
      else formData.set("description", "");
    }
    if (input.purpose !== undefined) {
      if (input.purpose) formData.set("purpose", input.purpose);
      else formData.set("purpose", "");
    }
    if (input.departmentId !== undefined) {
      formData.set("departmentId", input.departmentId ?? "");
    }
    if (input.status) formData.set("status", input.status);

    const res = await fetch(`${baseUrl}/${id}`, {
      method: "PUT",
      body: formData,
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update flow: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async remove(id: string) {
    const res = await fetch(`${baseUrl}/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete flow: ${res.statusText}`,
      );
    }
    return res.json();
  },

  // Steps methods
  async getSteps(flowId: string) {
    const res = await fetch(`${baseUrl}/${flowId}/steps`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch steps: ${res.statusText}`);
    return res.json();
  },

  async createStep(
    flowId: string,
    data: {
      stepNumber: number;
      title: string;
      description?: string;
      responsiblePositionId?: string;
      departmentId?: string;
      stepTypeId?: string;
      notes?: string;
      positionX?: number;
      positionY?: number;
    },
  ) {
    const res = await fetch(`${baseUrl}/${flowId}/steps`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create step: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async updateStep(
    stepId: string,
    data: {
      stepNumber?: number;
      title?: string;
      description?: string;
      responsiblePositionId?: string;
      departmentId?: string;
      stepTypeId?: string;
      notes?: string;
      positionX?: number;
      positionY?: number;
    },
  ) {
    const res = await fetch(`${baseUrl}/steps/${stepId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update step: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteStep(stepId: string) {
    const res = await fetch(`${baseUrl}/steps/${stepId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete step: ${res.statusText}`,
      );
    }
    return res.json();
  },

  // Connections methods
  async getConnections(flowId: string) {
    const res = await fetch(`${baseUrl}/${flowId}/connections`, {
      credentials: "include",
    });
    if (!res.ok)
      throw new Error(`Failed to fetch connections: ${res.statusText}`);
    return res.json();
  },

  async createConnection(
    flowId: string,
    data: {
      fromStepId: string;
      toStepId: string;
      label?: string;
    },
  ) {
    const res = await fetch(`${baseUrl}/${flowId}/connections`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create connection: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteConnections(flowId: string) {
    const res = await fetch(`${baseUrl}/${flowId}/connections`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete connections: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async updateConnection(
    connectionId: string,
    data: {
      fromStepId?: string;
      toStepId?: string;
      label?: string | null;
    },
  ) {
    const res = await fetch(`${baseUrl}/connections/${connectionId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update connection: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteConnection(connectionId: string) {
    const res = await fetch(`${baseUrl}/connections/${connectionId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete connection: ${res.statusText}`,
      );
    }
    return res.json();
  },

  // Mapping methods
  async getMappings(flowId: string) {
    const res = await fetch(`${baseUrl}/${flowId}/mappings`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Failed to fetch mappings: ${res.statusText}`);
    return res.json();
  },

  async createMapping(
    flowId: string,
    data: {
      sopId?: string | null;
      departmentId?: string | null;
      positionId?: string | null;
    },
  ) {
    const res = await fetch(`${baseUrl}/${flowId}/mappings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to create mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async updateMapping(
    mappingId: string,
    data: {
      sopId?: string | null;
      departmentId?: string | null;
      positionId?: string | null;
    },
  ) {
    const res = await fetch(`${baseUrl}/mappings/${mappingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to update mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },

  async deleteMapping(mappingId: string) {
    const res = await fetch(`${baseUrl}/mappings/${mappingId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(
        error.error || `Failed to delete mapping: ${res.statusText}`,
      );
    }
    return res.json();
  },
};
